import { useGlobalContext } from 'GlobalContext';
import { createContext, useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import { UpdateManagedAccountRequestData } from 'services/interfaces/managedAccount';
import {
  disableManagedAccount,
  updateManagedAccount,
} from 'services/managedAccount';
import { validateCNPJ, validateCPF } from 'shared/helpers/document';
import { validateEmail } from 'shared/helpers/email';
import { onlyNumbers } from 'shared/helpers/string';
import { Contract, IManagedAccount } from 'shared/models/contract';
import ManagedAccountModal from './ManagedAccountModal';

interface ManagedAccountErrors {
  managed_account_document?: string;
  managed_account_trade_name?: string;
  managed_account_phone_number?: string;
  managed_account_email?: string;
  managed_account_alias?: string;
}

interface ManagedAccountModalProps {
  isOpen: boolean;
  onClose(): void;
  contract: Contract;
}

interface ManagedAccountModalContextData {
  loading: boolean;
  setLoading(b: boolean): void;
  errors: ManagedAccountErrors;
  account: IManagedAccount | null;
  setAccount(a: IManagedAccount): void;
  handleSubmit(): void;
  handleDisableAccount(account: IManagedAccount): void;
}

const Context = createContext<ManagedAccountModalContextData>(
  {} as ManagedAccountModalContextData,
);

export const useManagedAccountModalContext = () => useContext(Context);

const ManagedAccountModalContext: React.FC<ManagedAccountModalProps> = ({
  isOpen,
  onClose,
  contract,
}) => {
  const { confirm } = useGlobalContext();

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<ManagedAccountErrors>({});
  const [account, setAccount] = useState<IManagedAccount | null>(
    contract.managedAccount || null,
  );

  useEffect(() => {
    if (isOpen && !account) {
      if (contract.managedAccount) {
        setAccount(contract.managedAccount);
      } else {
        const defaultAccount: IManagedAccount = {
          is_managed_account: false,
          managed_account_document: '',
          managed_account_trade_name: '',
          managed_account_phone_number: '',
          managed_account_email: '',
          managed_account_alias: '',
        };
        setAccount(defaultAccount);
      }
    }
  }, [isOpen, account, contract.managedAccount]);

  const validatePayload = (
    payload: UpdateManagedAccountRequestData,
  ): ManagedAccountErrors => {
    const errors: ManagedAccountErrors = {};

    if (!payload.managed_account_document) {
      errors.managed_account_document = 'Documento é obrigatório';
    } else if (
      !validateCNPJ(onlyNumbers(payload.managed_account_document)) &&
      !validateCPF(onlyNumbers(payload.managed_account_document))
    ) {
      errors.managed_account_document = 'Insira um documento válido';
    }

    if (!payload.managed_account_trade_name) {
      errors.managed_account_trade_name = 'Razão social é obrigatório';
    }

    if (!payload.managed_account_phone_number) {
      errors.managed_account_phone_number = 'Telefone é obrigatório';
    } else {
      const phoneDigits = onlyNumbers(payload.managed_account_phone_number);
      if (phoneDigits.length !== 10 && phoneDigits.length !== 11) {
        errors.managed_account_phone_number =
          'Insira um telefone válido (10 ou 11 dígitos)';
      }
    }

    if (!payload.managed_account_email) {
      errors.managed_account_email = 'Email é obrigatório';
    } else if (!validateEmail(payload.managed_account_email)) {
      errors.managed_account_email = 'Insira um email válido';
    }

    if (!payload.managed_account_alias) {
      errors.managed_account_alias = 'Apelido é obrigatório';
    }

    return errors;
  };

  const handleSubmit = () => {
    if (!account || !contract || !contract.id) {
      console.error('Handle Submit Error: Invalid data');
      toast.error('Falha ao desvincular a conta gerenciável');
      return;
    }

    account.is_managed_account = true;

    const payload: UpdateManagedAccountRequestData = {
      contract_id: contract.id,
      ...account,
    };

    const errors = validatePayload(payload);

    setErrors(errors);

    if (Object.values(errors).length) {
      console.error('Handle Submit Error: Validation errors');
      return;
    }

    setLoading(true);

    updateManagedAccount(payload)
      .then(() => {
        toast.success('Conta gerenciável salva com sucesso');
        onClose();
      })
      .catch((e: AxiosError) => {
        toast.error('Falha ao salvar a conta gerenciável');
        console.error(
          'Falha ao salvar a conta gerenciável',
          e?.response?.data
            ? JSON.stringify(e?.response?.data, null, 2)
            : JSON.stringify(e?.response, null, 2),
        );
      })
      .finally(() => setLoading(false));
  };

  const handleDisableAccount = (account: IManagedAccount) => {
    if (!contract.id || account.is_managed_account === false) {
      toast.error(
        'O contrato precisa ter uma conta vinculada para desvincular',
      );
      return;
    }

    const payload: UpdateManagedAccountRequestData = {
      contract_id: contract.id,
      ...account,
      is_managed_account: false,
    };

    confirm('Desvincular conta gerenciável', 'Deseja continuar?', () => {
      setLoading(true);

      disableManagedAccount(payload)
        .then(() => {
          toast.success('Conta desvinculada com sucesso');
          onClose();
        })
        .catch((e: AxiosError) => {
          toast.error('Falha ao desvincular a conta gerenciável');
          console.error(
            'Falha ao desvincular a conta gerenciável',
            e?.response?.data
              ? JSON.stringify(e?.response?.data, null, 2)
              : JSON.stringify(e?.response, null, 2),
          );
        })
        .finally(() => setLoading(false));
    });
  };

  const data: ManagedAccountModalContextData = {
    loading,
    setLoading,
    errors,
    account,
    setAccount,
    handleSubmit,
    handleDisableAccount,
  };

  return (
    <Context.Provider value={data}>
      <ManagedAccountModal isOpen={isOpen} onClose={onClose} />
    </Context.Provider>
  );
};

export default ManagedAccountModalContext;
